// Visual GovCon graph: a reactflow node-link canvas. Nodes are draggable,
// colored by entity type; relationships are drawn by dragging from a node's
// right handle to another node's left handle (onConnect bubbles up so the panel
// can collect type/source/confidence). Clicking a node or edge selects it for
// the side actions. This is the "proposal-person-friendly" view that replaces
// the old form-and-rows layout; the data still flows through the same graph
// hooks unchanged.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useCallback, useEffect, useRef } from 'react';
import ReactFlow, { Background, Controls, Handle, MarkerType, Position, useEdgesState, useNodesState } from 'reactflow';
import 'reactflow/dist/style.css';
import { ringPosition } from './graphLayout';
// Tailwind classes per node type (background + border). Unknown types fall back
// to the company palette.
const NODE_STYLE = {
    company: 'bg-blue-50 border-blue-300 text-blue-900',
    gov_org: 'bg-emerald-50 border-emerald-300 text-emerald-900',
    vehicle: 'bg-amber-50 border-amber-300 text-amber-900',
    partner: 'bg-purple-50 border-purple-300 text-purple-900',
    person: 'bg-slate-50 border-slate-300 text-slate-900'
};
function EntityNodeComp({ data, selected }) {
    const palette = NODE_STYLE[data.nodeType] ?? NODE_STYLE.company;
    const ring = selected ? ' ring-2 ring-primary' : data.highlighted ? ' ring-2 ring-blue-500' : '';
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.captureGraph.nodeRow,
        "data-node-id": data.id,
        "data-node-type": data.nodeType,
        className: `rounded-full border px-4 py-2 text-xs font-medium shadow-sm ${palette}${ring}`,
        children: [
            /*#__PURE__*/ _jsx(Handle, {
                type: "target",
                position: Position.Left,
                className: "!h-2 !w-2"
            }),
            data.label,
            /*#__PURE__*/ _jsx(Handle, {
                type: "source",
                position: Position.Right,
                className: "!h-2 !w-2"
            })
        ]
    });
}
// Stable nodeTypes reference (reactflow warns if this is rebuilt each render).
const nodeTypes = {
    entity: EntityNodeComp
};
function edgeStyle(overrideStatus) {
    if (overrideStatus === 'user_confirmed') return {
        stroke: '#16a34a',
        strokeWidth: 2
    };
    if (overrideStatus === 'superseded') return {
        stroke: '#9ca3af',
        strokeDasharray: '4 4'
    };
    return {
        stroke: '#64748b'
    };
}
function isHighlighted(n, ref) {
    if (!ref) return false;
    return n.id === ref || n.ryuguRef === ref;
}
export function GraphCanvas({ nodes, edges, highlightRef, selectedNodeId, onSelectNode, onSelectEdge, onConnectEdge }) {
    const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
    const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
    // Remembers positions across data refetches so a refetch doesn't reshuffle a
    // graph the user has arranged.
    const posRef = useRef(new Map());
    useEffect(()=>{
        setRfNodes((prev)=>{
            const prevPos = new Map(prev.map((n)=>[
                    n.id,
                    n.position
                ]));
            return nodes.map((n, i)=>{
                const pos = prevPos.get(n.id) ?? posRef.current.get(n.id) ?? ringPosition(nodes.length, i);
                posRef.current.set(n.id, pos);
                return {
                    id: n.id,
                    type: 'entity',
                    position: pos,
                    selected: n.id === selectedNodeId,
                    data: {
                        id: n.id,
                        label: n.displayName,
                        nodeType: n.nodeType,
                        highlighted: isHighlighted(n, highlightRef)
                    }
                };
            });
        });
    }, [
        nodes,
        highlightRef,
        selectedNodeId,
        setRfNodes
    ]);
    useEffect(()=>{
        setRfEdges(edges.map((e)=>({
                id: e.id,
                source: e.fromNode,
                target: e.toNode,
                label: `${e.edgeType} ${Math.round(e.confidence * 100)}%`,
                markerEnd: {
                    type: MarkerType.ArrowClosed
                },
                style: edgeStyle(e.overrideStatus),
                labelStyle: {
                    fontSize: 10
                }
            })));
    }, [
        edges,
        setRfEdges
    ]);
    const handleConnect = useCallback((c)=>{
        if (c.source && c.target && c.source !== c.target) onConnectEdge(c.source, c.target);
    }, [
        onConnectEdge
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: "h-[460px] w-full overflow-hidden rounded-md border bg-muted/10",
        "data-testid": Testids.captureGraph.canvasRoot,
        children: /*#__PURE__*/ _jsxs(ReactFlow, {
            nodes: rfNodes,
            edges: rfEdges,
            onNodesChange: onNodesChange,
            onEdgesChange: onEdgesChange,
            onConnect: handleConnect,
            onNodeClick: (_, node)=>onSelectNode(nodes.find((n)=>n.id === node.id) ?? null),
            onEdgeClick: (_, edge)=>onSelectEdge(edges.find((e)=>e.id === edge.id) ?? null),
            onPaneClick: ()=>{
                onSelectNode(null);
                onSelectEdge(null);
            },
            nodeTypes: nodeTypes,
            fitView: true,
            minZoom: 0.2,
            children: [
                /*#__PURE__*/ _jsx(Background, {}),
                /*#__PURE__*/ _jsx(Controls, {
                    showInteractive: false
                })
            ]
        })
    });
}
